import React from 'react'
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NotFound from 'modules/reports/components/NotFound'
import SaveModal, { SAVE_ACTION } from 'modules/reports/components/SaveReportModal'
import ReportsSqlEditor from 'modules/reports/components/SqlEditor'
import { reportStoreContext } from 'modules/reports/contexts/reportStoreContext'
import useReport from 'modules/reports/hooks/useReport'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import useStore from 'modules/reports/hooks/useStore'
import type { RouteContextParams } from 'modules/reports/lib/RouterContextParams'
import { PromptProvider } from 'modules/reports/lib/promptContext'

const EditSqlReportPage = () => {
  const params = useParams()
  const [search, _] = useSearchParams()
  const { loading } = useOutletContext<RouteContextParams>()

  const { report, isNotFound } = useReport({
    id: String(params.id),
    loading,
  })

  const { loading: isFeatureLoading, featureAvailability } = useFeatureAvailability(
    Features.analytics_advanced_reports
  )

  const store = useStore({
    report,
    origin: report?.origin && report.origin == 'template' ? 'modified_template' : undefined,
  })
  const { track } = useReportTracking(store)

  useUnsafeEffect(() => {
    if (!report) return

    track(trackingEvents.reports.reportsSqlEditVisited)
    track(trackingEvents.reports.openCustomerReport)
  }, [track, report])

  const isAuthorized =
    // nosemgrep
    featureAvailability.available

  const showSaveModal = search.get('action') == SAVE_ACTION

  return (
    <ResponsivePage.Body
      loading={loading || isFeatureLoading}
      authorized={isAuthorized}
      error={undefined}
      showHeader={false}
    >
      <NotFound notFound={isNotFound}>
        <reportStoreContext.Provider value={store}>
          <PromptProvider>
            <Box flexDirection="column">
              {showSaveModal && report && store && <SaveModal report={report} store={store} />}
              {store && <ReportsSqlEditor store={store} />}
            </Box>
          </PromptProvider>
        </reportStoreContext.Provider>
      </NotFound>
    </ResponsivePage.Body>
  )
}

export default EditSqlReportPage
